import type { ParcelDetail as ParcelDetailType } from './api';

// Score computation helpers (mirror server-side logic)
export function computePowerScore(p: ParcelDetailType): number {
  const miles = p.nearest_substation_miles != null ? Number(p.nearest_substation_miles) : null;
  const kv = p.nearest_substation_kv != null ? Number(p.nearest_substation_kv) : null;
  if (miles != null && miles < 5 && kv != null && kv >= 138) return 2;
  return 0;
}

export function computeAcreageScore(p: ParcelDetailType): number {
  const ac = p.acreage != null ? Number(p.acreage) : 0;
  if (ac >= 2500) return 15;
  if (ac >= 1000) return 12;
  if (ac >= 500) return 8;
  if (ac >= 200) return 5;
  if (ac >= 100) return 3;
  return 0;
}

export function computeGasSupplyScore(p: ParcelDetailType): number {
  const mw = p.pipeline_available_mw != null ? Number(p.pipeline_available_mw) : 0;
  if (mw >= 200) return 35;
  if (mw >= 100) return 30;
  if (mw >= 50) return 25;
  if (mw >= 25) return 18;
  if (mw >= 10) return 10;
  if (mw >= 1) return 5;
  return 0;
}

export function computeFloodScore(p: ParcelDetailType): number {
  if (!p.flood_zone || ['X', 'B', 'C'].includes(p.flood_zone)) return 5;
  return 0;
}

export function computeSlopeScore(p: ParcelDetailType): number {
  const slope = p.avg_slope_pct != null ? Number(p.avg_slope_pct) : null;
  if (slope != null && slope < 2) return 10;
  if (slope != null && slope < 5) return 5;
  return 0;
}

export function computeEpaScore(p: ParcelDetailType): number {
  return p.epa_ozone_attainment !== false ? 15 : 0;
}

export function computeHighwayScore(p: ParcelDetailType): number {
  const miles = p.nearest_highway_miles != null ? Number(p.nearest_highway_miles) : null;
  if (miles != null && miles < 2) return 5;
  if (miles != null && miles < 5) return 3;
  if (miles != null && miles < 10) return 1;
  return 0;
}

export function computeGasPlantScore(p: ParcelDetailType): number {
  const miles = p.nearest_gas_plant_miles != null ? Number(p.nearest_gas_plant_miles) : null;
  if (miles != null && miles < 2) return 3;
  if (miles != null && miles < 5) return 2;
  if (miles != null && miles < 15) return 1;
  return 0;
}

export function computePipelineInfraScore(p: ParcelDetailType): number {
  // Scale 0-10 to 0-15 to match BTM weighting
  return p.pipeline_infra_score != null ? Math.min(15, Math.round(Number(p.pipeline_infra_score) * 1.5)) : 0;
}

// Display helpers
export function formatMW(mw: number): string {
  if (mw >= 1000) return `${(mw / 1000).toFixed(1)} GW`;
  if (mw >= 10) return `${Math.round(mw)} MW`;
  if (mw >= 1) return `${mw.toFixed(1)} MW`;
  return `${Math.round(mw * 1000)} kW`;
}

export function formatMbps(mbps: number): string {
  if (mbps >= 1000) return `${(mbps / 1000).toFixed(mbps % 1000 === 0 ? 0 : 1)} Gbps`;
  return `${mbps} Mbps`;
}

// DB column `parcels.aquifer_name` uses SCREAMING_SNAKE (GULF_COAST, EDWARDS-TRINITY).
// Display surfaces want title-case ("Gulf Coast", "Edwards-Trinity").
// Inputs already containing lowercase letters (e.g. legacy hardcoded fallbacks or
// brackish-zone names like "Pecos Valley Alluvium") pass through unchanged.
export function formatAquiferName(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (/[a-z]/.test(raw)) return raw;
  return raw.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

// FCC BDC business_residential_code: 'R' residential, 'B' business, 'X' both-types.
// Only 'B' confirms the carrier actually sells a business-class product at this location
// — 'X' just means the same provider also serves residential, which doesn't guarantee
// dedicated/SLA-backed service. Treat 'B' as the only reliable enterprise signal.
export function isEnterpriseBrc(brc?: string | null): boolean {
  return brc === 'B';
}

export function getMWColor(mw: number): string {
  if (mw >= 200) return '#7c3aed';
  if (mw >= 50) return '#16a34a';
  if (mw >= 10) return '#ca8a04';
  return '#6b7280';
}

export function getSlopeColor(slope: number): string {
  if (slope < 2) return '#16a34a';
  if (slope < 5) return '#ca8a04';
  return '#dc2626';
}

export function getSubstationColor(miles: number): string {
  if (miles <= 5) return '#16a34a';
  if (miles <= 15) return '#ca8a04';
  return '#dc2626';
}

export function getReliabilityColor(pct: number | null): string {
  if (pct == null) return '#6b7280';
  if (pct > 70) return '#16a34a';
  if (pct > 30) return '#ca8a04';
  return '#dc2626';
}

export function getFloodColor(zone: string): string {
  return isHighRiskFloodZone(zone) ? '#dc2626' : '#16a34a';
}

export function isHighRiskFloodZone(zone: string): boolean {
  return ['A', 'AE', 'AH', 'AO', 'A99', 'AR', 'VE', 'V'].includes(zone);
}

// Flood severity gradient — centralizes the threshold logic for the briefing UX.
//
// Today's binary (zone-code-or-NULL) flips Paces (22% rep parcel) to a hard FAIL,
// which over-states risk. The pct column from migration 141 lets us soften:
//
//   0%       → green ADDRESSED   "No mapped flood zone"
//   0–10%    → green ADDRESSED   "X% drainage corridor; Y% upland buildable"
//   10–30%   → amber INQUIRY     "X% in Zone A drainage"
//   30%+     → red FLAGGED       "X% in Zone A — major siting constraint"
//
// When the parcel is a member of a curated carve-out, we read the carve-out's
// flood_zone_pct_100yr (lower because the assembly is geographically larger
// than the rep parcel alone — Paces' 4-parcel block is 8.4% vs the rep's 22%).
//
// NULL pct + no zone code = the parcel sits outside FEMA-mapped tile coverage
// (e.g. East TX before the prod re-ingest). We collapse to "clear" rather
// than "unknown" because the binary FEMA-no-mapping state used to be REQUIRES
// INQUIRY, which we're explicitly softening per the May 23 demo plan.
export type FloodSeverityLevel = 'clear' | 'mostly_clear' | 'partial' | 'significant' | 'severe';

export interface FloodSeverity {
  level: FloodSeverityLevel;
  label: string;          // 1-3 word headline (hero card)
  color: string;          // hex — palette-matched (REPORT.pass/warn/fail in BriefingContent)
  ddStatus: 'ADDRESSED' | 'REQUIRES INQUIRY' | 'FLAGGED';
  subLine: string;        // full sentence for sub-line / DD-checklist summary
  pct: number | null;     // the pct value used for thresholding (carve-out preferred over parcel)
  source: 'carve_out' | 'parcel' | 'none';
  // Derived acres — when totalAcres is known we present absolute acres in the
  // sub-line because IC underwriting is "how many acres can I build on", not
  // "what's the flood %". Pct stays in the audit tile and the limits sentence.
  // CAD-denominated: pct × CAD acreage. Slight mismatch vs the PostGIS-derived
  // numerator (Paces stored 8.41% × 2371.85 ac → 199 ac, vs PostGIS direct 195 ac;
  // ~2% drift from CAD vs GIS measurement noise — fine for IC display).
  totalAcres: number | null;
  floodAcres: number | null;
  buildableAcres: number | null;
}

interface FloodSeverityCarveOut {
  parcelIds: string[];
  totalAcres: number | null;
  floodZonePct100yr: number | null;
}

interface FloodSeverityParcel {
  id: string;
  flood_zone: string | null;
  flood_zone_pct_100yr: number | null;
  acreage?: number | string | null;
}

export function floodSeverity(
  parcel: FloodSeverityParcel,
  carveOut?: FloodSeverityCarveOut | null
): FloodSeverity {
  // Muted "Blue Ridge Hazier" palette — mirrors REPORT.pass/caution/fail in
  // PrintAtoms.tsx so hero-card severity reads as part of the briefing's
  // foggy-ridge design language, not bright web-status green.
  const PASS = '#5C6B45';   // moss/olive — REPORT.pass
  const WARN = '#9A7B30';   // warm amber — REPORT.caution
  const FAIL = '#A04030';   // terra-cotta — REPORT.fail

  // Carve-out pct wins when the parcel is one of its members and the pct has
  // been computed; otherwise fall back to the parcel's own pct; otherwise we
  // have no quantitative measurement and fall back to the binary zone code.
  const inCarveOut = !!(carveOut && carveOut.parcelIds && carveOut.parcelIds.includes(parcel.id));
  const carveOutPct = inCarveOut ? carveOut!.floodZonePct100yr : null;
  const parcelPct = parcel.flood_zone_pct_100yr;
  const zoneCode = parcel.flood_zone;

  let pct: number | null = null;
  let source: FloodSeverity['source'] = 'none';
  if (carveOutPct != null) {
    pct = carveOutPct;
    source = 'carve_out';
  } else if (parcelPct != null) {
    pct = parcelPct;
    source = 'parcel';
  }

  // Acres denominator: carve-out total when in a carve-out, parcel acreage
  // otherwise. Both are CAD-sourced — same units, same denominator the IC
  // sees in the Site Acreage tile.
  const parcelAcres = parcel.acreage != null ? Number(parcel.acreage) : null;
  const totalAcres = source === 'carve_out' && carveOut?.totalAcres != null
    ? Number(carveOut.totalAcres)
    : (source === 'parcel' && parcelAcres != null ? parcelAcres : null);
  const floodAcres = pct != null && totalAcres != null
    ? (pct / 100) * totalAcres
    : null;
  const buildableAcres = totalAcres != null && floodAcres != null
    ? Math.max(0, totalAcres - floodAcres)
    : null;
  const scopeNoun = source === 'carve_out' ? 'carve-out' : 'parcel';
  const acres = (n: number) => Math.round(n).toLocaleString();

  // Fallback to binary zone code when no pct measurement is available
  // (county not yet covered by FEMA NFHL ingest or by migration-141 compute).
  if (pct == null) {
    if (zoneCode && isHighRiskFloodZone(zoneCode)) {
      return {
        level: 'partial',
        label: 'Drainage corridor',
        color: WARN,
        ddStatus: 'REQUIRES INQUIRY',
        subLine: `FEMA Zone ${zoneCode} — drainage assessment recommended`,
        pct: null,
        source: 'parcel',
        totalAcres,
        floodAcres: null,
        buildableAcres: null,
      };
    }
    if (zoneCode) {
      return {
        level: 'clear',
        label: 'Minimal flood risk',
        color: PASS,
        ddStatus: 'ADDRESSED',
        subLine: `FEMA Zone ${zoneCode} — minimal flood risk`,
        pct: null,
        source: 'parcel',
        totalAcres,
        floodAcres: null,
        buildableAcres: null,
      };
    }
    // No pct AND no zone code = FEMA has published no digital NFHL covering this
    // parcel. That is the absence of a map, not a finding of low risk, and it must
    // not read as ADDRESSED/clear — doing so rendered "No FEMA-mapped flood zone"
    // as a green hero card for every parcel in a county FEMA has never mapped
    // (Frio, 2026-08: DFIRM 48163C carries zero polygons). Unknown is an inquiry.
    return {
      level: 'partial',
      label: 'Flood hazard unmapped',
      color: WARN,
      ddStatus: 'REQUIRES INQUIRY',
      subLine: 'No FEMA digital flood mapping published for this county — hazard undetermined; survey-grade topography and drainage screen required before LOI',
      pct: null,
      source: 'none',
      totalAcres,
      floodAcres: null,
      buildableAcres: null,
    };
  }

  const pctRounded = Math.round(pct);
  const buildablePct = Math.max(0, 100 - pctRounded);
  const scopeLabel = source === 'carve_out' && carveOut?.totalAcres
    ? `${acres(Number(carveOut.totalAcres))}-ac carve-out`
    : null;
  const haveAcres = floodAcres != null && buildableAcres != null && totalAcres != null;

  if (pct === 0) {
    return {
      level: 'clear',
      label: 'No mapped flood zone',
      color: PASS,
      ddStatus: 'ADDRESSED',
      subLine: scopeLabel
        ? `${scopeLabel} clear of FEMA Zone A — 100% upland buildable`
        : 'No FEMA-mapped flood zone on parcel',
      pct,
      source,
      totalAcres,
      floodAcres,
      buildableAcres,
    };
  }

  if (pct < 10) {
    return {
      level: 'mostly_clear',
      label: 'Mostly clear',
      color: PASS,
      ddStatus: 'ADDRESSED',
      subLine: haveAcres
        ? `${acres(buildableAcres!)} ac upland buildable; ${acres(floodAcres!)} ac in Zone A drainage${scopeLabel ? ` (${scopeLabel})` : totalAcres != null ? ` (${acres(totalAcres)}-ac ${scopeNoun})` : ''}`
        : (scopeLabel
            ? `${pctRounded}% drainage corridor on ${scopeLabel}; ${buildablePct}% upland buildable`
            : `${pctRounded}% drainage corridor; ${buildablePct}% upland buildable`),
      pct,
      source,
      totalAcres,
      floodAcres,
      buildableAcres,
    };
  }

  if (pct < 30) {
    return {
      level: 'partial',
      label: 'Drainage corridor',
      color: WARN,
      ddStatus: 'REQUIRES INQUIRY',
      subLine: haveAcres
        ? `${acres(buildableAcres!)} ac upland buildable; ${acres(floodAcres!)} ac in FEMA Zone A drainage${scopeLabel ? ` (${scopeLabel})` : totalAcres != null ? ` (${acres(totalAcres)}-ac ${scopeNoun})` : ''}`
        : (scopeLabel
            ? `${pctRounded}% of ${scopeLabel} in FEMA Zone A drainage; ${buildablePct}% upland buildable`
            : `${pctRounded}% in FEMA Zone A drainage; ${buildablePct}% upland buildable`),
      pct,
      source,
      totalAcres,
      floodAcres,
      buildableAcres,
    };
  }

  return {
    level: 'severe',
    label: 'Major flood constraint',
    color: FAIL,
    ddStatus: 'FLAGGED',
    subLine: haveAcres
      ? `${acres(floodAcres!)} ac in FEMA Zone A on ${acres(totalAcres!)}-ac ${scopeNoun} — major siting constraint`
      : (scopeLabel
          ? `${pctRounded}% of ${scopeLabel} in FEMA Zone A — major siting constraint`
          : `${pctRounded}% in FEMA Zone A — major siting constraint`),
    pct,
    source,
    totalAcres,
    floodAcres,
    buildableAcres,
  };
}

export function isOutOfState(parcel: ParcelDetailType): boolean {
  if (!parcel.state || !parcel.mailing_address || parcel.mailing_address.length < 5) return false;
  return !new RegExp(`\\b${parcel.state}\\b`, 'i').test(parcel.mailing_address);
}

export function isEntityOwner(ownerName: string | null): boolean {
  if (!ownerName) return false;
  return /\b(LLC|LP|TRUST|ESTATE|INC|CORP|LTD|COMPANY|PARTNERS)\b/i.test(ownerName);
}

export function getEntityType(ownerName: string | null): string {
  if (!ownerName) return '';
  const match = ownerName.match(/\b(LLC|LP|TRUST|ESTATE|INC|CORP|LTD|COMPANY|PARTNERS)\b/i);
  return match ? match[1].toUpperCase() : '';
}
